(() => {
  const storageKey = 'nimbli_newsletter_popup_v1';
  const subscriptionDays = 180;
  const minimumDelay = 15000;
  const instances = new WeakMap();

  const now = () => Date.now();

  const readPreference = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey));
      if (!value || typeof value.until !== 'number') return null;
      if (value.until <= now()) {
        localStorage.removeItem(storageKey);
        return null;
      }
      return value;
    } catch (error) {
      return null;
    }
  };

  const writePreference = (state, days) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        state,
        until: now() + (days * 24 * 60 * 60 * 1000)
      }));
    } catch (error) {
      return;
    }
  };

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const anotherInterfaceIsOpen = (dialog) => {
    const activeDialog = Array.from(document.querySelectorAll('dialog[open]'))
      .some((element) => element !== dialog);
    const miniCart = document.querySelector('#desktop_cart.open-extend');
    const mobileMenu = document.querySelector('#mobile_menu.act, #mobile_menu[aria-hidden="false"]');
    const legacyModal = Array.from(document.querySelectorAll('.modal.show, [role="dialog"][aria-hidden="false"]'))
      .some((element) => element !== dialog && isVisible(element));
    return activeDialog || isVisible(miniCart) || isVisible(mobileMenu) || legacyModal;
  };

  const activeFooterForm = (dialog) => {
    const activeForm = document.activeElement?.closest('[data-nimbli-newsletter-form]');
    return activeForm && !dialog.contains(activeForm);
  };

  const initPopup = (section) => {
    if (!section || instances.has(section)) return;

    const dialog = section.querySelector('.nimbli-newsletter-popup__dialog');
    const closeButton = section.querySelector('[data-nimbli-newsletter-popup-close]');
    const emailInput = section.querySelector('input[type="email"]');
    if (!dialog || !closeButton || !emailInput || typeof dialog.showModal !== 'function') return;

    const designMode = section.dataset.designMode === 'true';
    const delay = Math.max(15, Number(section.dataset.delaySeconds) || 30) * 1000;
    const scrollPercent = Math.min(90, Math.max(25, Number(section.dataset.scrollPercent) || 50));
    const dismissDays = Math.max(1, Number(section.dataset.dismissDays) || 7);
    const success = section.querySelector('[data-nimbli-newsletter-success]');
    let previousFocus = null;
    let opened = false;
    let closing = false;
    let scrollReached = false;
    let minimumReached = designMode;
    let retryTimer = 0;
    let minimumTimer = 0;
    let delayTimer = 0;

    const stopTriggers = () => {
      window.clearTimeout(minimumTimer);
      window.clearTimeout(delayTimer);
      window.clearTimeout(retryTimer);
      window.removeEventListener('scroll', onScroll);
    };

    const focusableElements = () => Array.from(dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(isVisible);

    const restoreFocus = () => {
      if (previousFocus && previousFocus.isConnected && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
      previousFocus = null;
    };

    const finishClose = () => {
      if (dialog.open) dialog.close();
      document.body.classList.remove('nimbli-newsletter-popup-open');
      closing = false;
      restoreFocus();
    };

    const closePopup = (remember = true) => {
      if (!dialog.open || closing) return;
      closing = true;
      if (remember && !designMode && !success) writePreference('dismissed', dismissDays);
      dialog.classList.remove('is-visible');
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finishClose();
      } else {
        window.setTimeout(finishClose, 260);
      }
    };

    const openPopup = (reason, ignorePreference = false) => {
      if (opened || dialog.open) return;
      if (!ignorePreference && !designMode && readPreference()) return;
      if (anotherInterfaceIsOpen(dialog) || activeFooterForm(dialog)) {
        retryTimer = window.setTimeout(() => openPopup(reason), 5000);
        return;
      }

      opened = true;
      section.dataset.openedBy = reason;
      previousFocus = document.activeElement;
      stopTriggers();
      dialog.showModal();
      document.body.classList.add('nimbli-newsletter-popup-open');
      window.requestAnimationFrame(() => {
        dialog.classList.add('is-visible');
        emailInput.focus();
      });
    };

    const tryScrollOpen = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const currentPercent = (window.scrollY / scrollable) * 100;
      if (currentPercent >= scrollPercent) {
        scrollReached = true;
        if (minimumReached) openPopup('scroll');
      }
    };

    function onScroll() {
      tryScrollOpen();
    }

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePopup(true);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        closeButton.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onCancel = (event) => {
      event.preventDefault();
      closePopup(true);
    };

    const onCloseClick = () => closePopup(true);

    closeButton.addEventListener('click', onCloseClick);
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('keydown', onKeydown);

    if (success) {
      if (!designMode) writePreference('subscribed', subscriptionDays);
      openPopup('success', true);
    } else if (designMode) {
      openPopup('design-mode', true);
    } else if (!readPreference()) {
      window.addEventListener('scroll', onScroll, { passive: true });
      minimumTimer = window.setTimeout(() => {
        minimumReached = true;
        if (scrollReached) openPopup('scroll');
      }, minimumDelay);
      delayTimer = window.setTimeout(() => openPopup('time'), delay);
      tryScrollOpen();
    }

    const destroy = () => {
      stopTriggers();
      closeButton.removeEventListener('click', onCloseClick);
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('keydown', onKeydown);
      if (dialog.open) dialog.close();
      document.body.classList.remove('nimbli-newsletter-popup-open');
      instances.delete(section);
    };

    instances.set(section, { destroy });
  };

  const initialize = (root = document) => {
    const success = root.querySelector?.('[data-nimbli-newsletter-success]');
    if (success && !success.closest('[data-nimbli-newsletter-popup]')) {
      writePreference('subscribed', subscriptionDays);
    }
    root.querySelectorAll?.('[data-nimbli-newsletter-popup]').forEach(initPopup);
  };

  const unload = (event) => {
    const section = event.target.querySelector?.('[data-nimbli-newsletter-popup]');
    const instance = section ? instances.get(section) : null;
    if (instance) instance.destroy();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', unload);
})();
